interface WithPresentationName {
  name: string
  displayName: string
  presentation?: { name?: string | null } | null
}

interface WithPresentationImage {
  image?: string | null
  presentation?: { image?: string | null } | null
  servedImageUrl?: string | null
}

/**
 * Returns the guest-facing name: presentation override if set, otherwise displayName.
 * @param resource
 */
export const getDisplayName = <T extends WithPresentationName>(
  resource: T
): string => resource.presentation?.name ?? resource.displayName

/**
 * Returns the internal name if set, otherwise the guest-facing name.
 * @param resource
 */
export const getNameOrOverride = <T extends WithPresentationName>(
  resource: T
): string => resource.name || getDisplayName(resource)

/**
 * Returns the guest-facing image: presentation override if set, otherwise native image.
 * @param resource
 */
export const getDisplayImage = <T extends WithPresentationImage>(
  resource: T
): string | null | undefined => resource.presentation?.image ?? resource.image

/**
 * Returns the photo guests are served. Callers needing the partner's own photo,
 * such as image-generation input, must use {@link getDisplayImage} instead.
 * @param resource
 */
export const getServedImage = <T extends WithPresentationImage>(
  resource: T
): string | null | undefined =>
  resource.servedImageUrl ?? getDisplayImage(resource)

interface WithPresentation {
  presentation?: Record<string, unknown> | null
}

/**
 * Whether a resource has any non-empty presentation overrides.
 * Returns false for null, undefined, or empty presentation values.
 * @param resource
 */
export const hasPresentation = <T extends WithPresentation>(
  resource: T
): boolean => {
  if (!resource.presentation) return false

  return Object.values(resource.presentation).some((value) => {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value).length > 0
    return true
  })
}
